import { memo, useCallback } from 'react'
import { useAuth } from '@/contexts/AuthContextSupabase'
import { useLanguage } from '@/hooks/useLanguageHook'
import { useTranslation } from '@/hooks/useTranslation'
import { Button } from 'primereact/button'
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup'
import { useNavigate } from 'react-router-dom'
import styles from './Header.module.css'

const SHOW_DEBUG_BUTTON = import.meta.env.VITE_SHOW_DEBUG_BUTTON === 'true'
const IS_DEV_ENV = import.meta.env.VITE_ENVIRONMENT === 'local'

export const Header = memo(() => {
  const { user, logout } = useAuth()
  const { language, toggleLanguage } = useLanguage()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const handleLogout = useCallback(async () => {
    try {
      await logout()
      navigate('/login')
    } catch (error) {
      console.error('Failed to logout:', error)
    }
  }, [logout, navigate])

  const confirmLogout = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      confirmPopup({
        target: event.currentTarget,
        message: t('logoutConfirmMessage'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: t('logout'),
        rejectLabel: t('cancel'),
        acceptClassName: 'p-button-danger',
        accept: handleLogout,
      })
    },
    [handleLogout, t]
  )

  return (
    <header className={styles.header}>
      <ConfirmPopup />
      <div className={styles.headerContent}>
        <div className={styles.title}>
          <i className="pi pi-shopping-cart" style={{ fontSize: '1.5rem' }} />
          <h1 className={styles.titleText}>{t('appTitle')}</h1>
          {IS_DEV_ENV && <span className={styles.devBadge}>DEV</span>}
        </div>

        <div className={styles.userSection}>
          <div className={styles.userInfo}>
            <i className="pi pi-user" style={{ fontSize: '16px' }} />
            <span className={styles.userEmail}>{user?.email || 'User'}</span>
          </div>
          {SHOW_DEBUG_BUTTON && (
            <div className={styles.languageToggleContainer}>
              <button
                onClick={toggleLanguage}
                className={styles.languageToggle}
                aria-label="Toggle Language"
                title={
                  language === 'en' ? 'Switch to Japanese' : 'Switch to English'
                }
              >
                <span
                  className={
                    language === 'en' ? styles.active : styles.inactive
                  }
                >
                  EN
                </span>
                <span className={styles.separator}>|</span>
                <span
                  className={
                    language === 'ja' ? styles.active : styles.inactive
                  }
                >
                  JP
                </span>
              </button>
            </div>
          )}
          <Button
            icon="pi pi-power-off"
            text
            severity="danger"
            onClick={confirmLogout}
            aria-label="Logout"
            tooltip={t('logout')}
            tooltipOptions={{ position: 'bottom' }}
          />
        </div>
      </div>
    </header>
  )
})

Header.displayName = 'Header'
